// frontend/src/components/admin/CompanyList.jsx
// ============================================================================
// LISTE DES ENTREPRISES — v6
// ✅ Onglet Utilisateurs dans le DetailPanel
// ✅ Reset mot de passe avec modal résultat
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Users, Lock, Unlock, Trash2, Edit,
  CheckCircle, XCircle, AlertCircle, Clock, Shield, Calendar,
  X, Hash, ChevronRight, FileText, KeyRound, Copy,
  Eye, EyeOff, RefreshCw, UserCheck, LogIn,
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import CompanyForm from './CompanyForm';
import DuplicationClientModal from './DuplicationClientModal';
import ConfirmDialog from '../ConfirmDialog';
import { Toast } from '../ui/Toast';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// UTILS
// ============================================================================
const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const formatDateLong = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtDatetime = (d) => {
  if (!d) return 'Jamais';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

// ============================================================================
// STATUS BADGE
// ============================================================================
const StatusBadge = ({ company, t }) => {
  if (company.is_blocked) return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide"
      style={{ backgroundColor: t.dangerSoft, color: t.dangerText }}>
      <Lock size={11} /> BLOQUÉE
    </span>
  );
  const cfgs = {
    active:    { bg: t.successSoft, color: t.successText, Icon: CheckCircle, label: 'ACTIVE' },
    trial:     { bg: t.primarySoft, color: t.primaryText, Icon: Clock,       label: 'ESSAI' },
    suspended: { bg: t.warningSoft, color: t.warningText, Icon: AlertCircle, label: 'SUSPENDUE' },
    expired:   { bg: t.bgTableHeader, color: t.textMuted, Icon: XCircle,     label: 'EXPIRÉE' },
  };
  const cfg = cfgs[company.status] || cfgs.active;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      <cfg.Icon size={11} /> {cfg.label}
    </span>
  );
};

// ============================================================================
// ROLE BADGE
// ============================================================================
const RoleBadge = ({ role, t }) => {
  const cfgs = {
    admin:    { label: 'Admin',      bg: '#448696' + '15', color: '#448696' },
    manager:  { label: 'Manager',    bg: '#7c3aed' + '15', color: '#7c3aed' },
    employee: { label: 'Employé',    bg: t.bgInput,        color: t.textMuted },
    rh:       { label: 'RH',         bg: '#059669' + '15', color: '#059669' },
  };
  const cfg = cfgs[role] || { label: role, bg: t.bgInput, color: t.textMuted };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
      background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
};

// ============================================================================
// MODAL RESET — Affiche le mot de passe temporaire généré
// ============================================================================
const ResetResultModal = ({ result, onClose, t }) => {
  const [copied, setCopied] = useState(false);
  const [show, setShow] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(result.tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,15,30,0.72)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460, borderRadius: 20, background: '#fff',
        overflow: 'hidden', boxShadow: '0 40px 80px rgba(0,0,0,0.25)',
        fontFamily: 'DM Sans, sans-serif' }}>

        {/* Barre top */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #448696, #fed774)' }} />

        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#448696' + '15',
              border: '1.5px solid #448696' + '25', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <KeyRound size={20} color="#448696" />
            </div>
            <div>
              <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                Mot de passe réinitialisé
              </p>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                {result.name} · {result.email}
              </p>
            </div>
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding: '20px 24px' }}>
          {/* Alerte */}
          <div style={{ padding: '12px 14px', borderRadius: 12, background: '#fffbeb',
            border: '1px solid #fde68a', marginBottom: 18, display: 'flex', gap: 10 }}>
            <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
              Ce mot de passe temporaire ne sera affiché <strong>qu'une seule fois</strong>.
              Copiez-le et transmettez-le à l'utilisateur. Il devra le changer à la prochaine connexion.
            </p>
          </div>

          {/* Mot de passe */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 8 }}>
              Mot de passe temporaire
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 16px', borderRadius: 12,
              background: '#f8fafc', border: '1.5px solid #e2e8f0' }}>
              <span style={{ flex: 1, fontSize: 18, fontWeight: 800, fontFamily: 'monospace',
                color: '#0f172a', letterSpacing: '2px', filter: show ? 'none' : 'blur(6px)',
                userSelect: show ? 'text' : 'none', transition: 'filter 0.2s' }}>
                {result.tempPassword}
              </span>
              <button onClick={() => setShow(s => !s)}
                style={{ padding: 6, borderRadius: 8, border: 'none', background: 'transparent',
                  color: '#64748b', cursor: 'pointer' }}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={handleCopy}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
                  borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  background: copied ? '#f0fdf4' : '#448696', color: copied ? '#059669' : 'white',
                  transition: 'all 0.2s' }}>
                <Copy size={13} />
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>

          {/* Info first_login */}
          <div style={{ padding: '10px 14px', borderRadius: 10, background: '#f0f9ff',
            border: '1px solid #bae6fd', display: 'flex', gap: 8 }}>
            <UserCheck size={14} color="#0891b2" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: '#0c4a6e', margin: 0, lineHeight: 1.5 }}>
              L'utilisateur sera contraint de changer son mot de passe dès la prochaine connexion.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px 20px', borderTop: '1px solid #f0f0f0',
          display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 24px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #448696, #367080)',
              color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(68,134,150,0.35)' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACTION BUTTON (inline table)
// ============================================================================
const ActionBtn = ({ icon: Icon, label, color, hoverBg, onClick, disabled }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(); }} disabled={disabled}
    className="p-1.5 rounded-md transition-all duration-100 disabled:opacity-30"
    style={{ color }} title={label}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; e.currentTarget.style.transform = 'scale(1.1)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}>
    <Icon size={15} strokeWidth={2} />
  </button>
);

// ============================================================================
// PANEL BTN (footer du slide-over)
// ============================================================================
const PanelBtn = ({ icon: Icon, label, onClick, disabled, bg, border, color, hoverBg, hoverColor }) => (
  <button onClick={onClick} disabled={disabled}
    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-bold border transition-all duration-150 disabled:opacity-40 active:scale-[0.97]"
    style={{ backgroundColor: bg, borderColor: border, color }}
    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.backgroundColor = hoverBg; e.currentTarget.style.color = hoverColor; e.currentTarget.style.borderColor = hoverBg; } }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = bg; e.currentTarget.style.color = color; e.currentTarget.style.borderColor = border; }}>
    <Icon size={14} /> {label}
  </button>
);

// ============================================================================
// INFO ROW
// ============================================================================
const InfoRow = ({ icon: Icon, label, value, t, mono }) => (
  <div className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: t.primarySofter }}>
    <div className="flex items-center gap-2">
      <Icon size={13} style={{ color: t.textMuted }} />
      <span className="text-[12px] font-semibold" style={{ color: t.textSecondary }}>{label}</span>
    </div>
    <span className={`text-[13px] font-bold ${mono ? 'font-mono' : ''}`} style={{ color: t.text }}>{value}</span>
  </div>
);

// ============================================================================
// SLIDE-OVER DETAIL PANEL

// ============================================================================
// CHANGER L'IDENTIFIANT D'UN COMPTE
// ----------------------------------------------------------------------------
// L'adresse de connexion se corrige : une faute de frappe a la creation, un
// changement de nom de domaine, un compte repris par quelqu'un d'autre. Le mot
// de passe n'est pas touche ici — c'est un autre geste, avec d'autres
// consequences.
// ============================================================================
const IdentifiantModal = ({ company, user, t, onClose, onSaved }) => {
  const [email, setEmail] = useState(user.email || '');
  const [nom, setNom] = useState(user.name || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const champ = {
    background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
    padding: '9px 12px', fontSize: 13, color: t.text, outline: 'none',
    fontFamily: 'inherit', width: '100%'
  };

  const enregistrer = async () => {
    setErreur(null);
    const adresse = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adresse)) return setErreur('Adresse e-mail invalide');
    if (!nom.trim()) return setErreur('Le nom ne peut pas être vide');

    setEnvoi(true);
    try {
      const res = await superAdminApi.companies.changerIdentifiant(company.id, user.id, {
        email: adresse, name: nom.trim()
      });
      onSaved(res.data || { email: adresse, name: nom.trim() });
    } catch (e) {
      setErreur(e.message || 'La modification a échoué');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: t.bgOverlay, zIndex: 70,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget && !envoi) onClose(); }}>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16,
        width: 'min(460px, 100%)', boxShadow: t.shadowLg }}>

        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.borderLight}` }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: t.text,
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit size={16} style={{ color: t.primary }} />
            Identifiant de connexion
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: t.textMuted }}>
            {company.company_name} — actuellement {user.email}
          </p>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {erreur && (
            <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 10,
              padding: '10px 12px', fontSize: 13, color: t.dangerText }}>{erreur}</div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Adresse de connexion</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@entreprise.com" style={champ} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>Nom affiché</span>
            <input value={nom} onChange={(e) => setNom(e.target.value)} style={champ} />
          </label>

          <p style={{ margin: 0, fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>
            Le mot de passe n&rsquo;est pas modifié. Les sessions ouvertes de ce compte sont
            fermées : la prochaine connexion se fera avec la nouvelle adresse.
          </p>
        </div>

        <div style={{ padding: '14px 20px', borderTop: `1px solid ${t.borderLight}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} disabled={envoi}
            style={{ ...champ, width: 'auto', cursor: 'pointer', fontWeight: 600, minWidth: 100 }}>
            Annuler
          </button>
          <button type="button" onClick={enregistrer} disabled={envoi}
            style={{ background: t.primary, border: `1px solid ${t.primary}`, borderRadius: 10,
              padding: '9px 18px', fontSize: 13, fontWeight: 700, color: '#fff',
              cursor: envoi ? 'default' : 'pointer', fontFamily: 'inherit', minWidth: 130 }}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
const DetailPanel = ({ company, t, onClose, onEdit, onManageLicence, onBlock, onUnblock, onDelete, onDupliquer, loading }) => {
  const [activeTab,    setActiveTab]    = useState('infos');
  const [users,        setUsers]        = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [resetting,    setResetting]    = useState(null);
  const [resetResult,  setResetResult]  = useState(null);
  const [compteAEditer, setCompteAEditer] = useState(null);

  const loadUsers = useCallback(async () => {
    if (!company) return;
    setLoadingUsers(true);
    try {
      const res = await superAdminApi.companies.getUsers(company.id);
      if (res.success) setUsers(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingUsers(false); }
  }, [company]);

  useEffect(() => {
    if (activeTab === 'users') loadUsers();
  }, [activeTab, loadUsers]);

  const handleReset = async (user) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${user.name} ?`)) return;
    setResetting(user.id);
    try {
      const res = await superAdminApi.companies.resetUserPassword(company.id, user.id);
      if (res.success) setResetResult(res.data);
    } catch (e) { console.error(e); }
    finally { setResetting(null); }
  };

  if (!company) return null;

  const TABS = [
    { key: 'infos', label: 'Infos' },
    { key: 'users', label: `Utilisateurs (${company.user_count || 0})` },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: t.bgOverlay }} onClick={onClose} />

      <div className="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col border-l"
        style={{ backgroundColor: t.mode === 'dark' ? '#0f1319' : '#ffffff',
          borderColor: t.border, boxShadow: '-8px 0 30px rgba(0,0,0,0.15)',
          animation: 'slideInRight 0.25s ease-out' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: t.border }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDark})` }}>
              <Building2 size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold truncate" style={{ color: t.text }}>{company.company_name}</h3>
              <p className="text-[12px] font-mono" style={{ color: t.textMuted }}>{company.company_slug}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors" style={{ color: t.textMuted }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <X size={18} />
          </button>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${t.border}`, padding: '0 24px', background: t.bgCard }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '10px 14px', border: 'none', background: 'transparent',
                borderBottom: activeTab === tab.key ? `2px solid ${t.primary}` : '2px solid transparent',
                color: activeTab === tab.key ? t.primary : t.textMuted,
                fontSize: 12, fontWeight: activeTab === tab.key ? 700 : 500,
                cursor: 'pointer', marginBottom: -1 }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── ONGLET INFOS ── */}
          {activeTab === 'infos' && (
            <div className="space-y-5">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: t.textMuted }}>Statut</div>
                <StatusBadge company={company} t={t} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: t.textMuted }}>Informations</div>
                <div className="space-y-2">
                  <InfoRow icon={Hash}      label="ID"           value={company.id}                                   t={t} mono />
                  <InfoRow icon={Users}     label="Utilisateurs" value={company.user_count || 0}                      t={t} />
                  <InfoRow icon={Calendar}  label="Créée le"     value={formatDateLong(company.created_at)}           t={t} />
                  <InfoRow icon={Clock}     label="Expiration"   value={company.trial_ends_at ? formatDateLong(company.trial_ends_at) : 'Aucune'} t={t} />
                  {company.licence_type && <InfoRow icon={FileText} label="Licence" value={company.licence_type} t={t} />}
                </div>
              </div>
            </div>
          )}

          {/* ── ONGLET UTILISATEURS ── */}
          {activeTab === 'users' && (
            <div>
              {loadingUsers ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 10 }}>
                  <RefreshCw size={16} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 13, color: t.textMuted }}>Chargement...</span>
                </div>
              ) : users.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>
                  <Users size={32} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                  <p style={{ fontSize: 13 }}>Aucun utilisateur</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {users.map(user => (
                    <div key={user.id} style={{ padding: '12px 14px', borderRadius: 12,
                      background: t.bgCard, border: `1px solid ${t.border}`,
                      display: 'flex', alignItems: 'center', gap: 10 }}>

                      {/* Avatar */}
                      <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: `linear-gradient(135deg, ${t.primary}20, ${t.primary}10)`,
                        border: `1px solid ${t.primary}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 800, color: t.primary }}>
                        {(user.name || '?')[0].toUpperCase()}
                      </div>

                      {/* Infos */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.name}
                          </p>
                          <RoleBadge role={user.role} t={t} />
                        </div>
                        <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{user.email}</p>
                        <p style={{ fontSize: 10, color: t.textMuted, margin: '2px 0 0',
                          display: 'flex', alignItems: 'center', gap: 3 }}>
                          <LogIn size={9} />
                          {fmtDatetime(user.last_login_at)}
                        </p>
                      </div>

                      {/* Statut */}
                      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                          background: user.status === 'active' ? '#f0fdf4' : '#fef2f2',
                          color: user.status === 'active' ? '#059669' : '#dc2626',
                          border: `1px solid ${user.status === 'active' ? '#a7f3d0' : '#fecaca'}` }}>
                          {user.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>

                        {/* Bouton reset */}
                        <button onClick={() => handleReset(user)}
                          disabled={resetting === user.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 4,
                            padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${t.border}`, background: t.bgInput,
                            fontSize: 10, fontWeight: 700, color: t.textSecondary,
                            opacity: resetting === user.id ? 0.5 : 1, transition: 'all 0.15s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#448696'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#448696'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = t.bgInput; e.currentTarget.style.color = t.textSecondary; e.currentTarget.style.borderColor = t.border; }}>
                          {resetting === user.id
                            ? <RefreshCw size={10} style={{ animation: 'spin 0.8s linear infinite' }} />
                            : <KeyRound size={10} />}
                          Réinitialiser
                        </button>

                        {/* L'adresse de connexion se corrige aussi : jusqu'ici
                            seul le mot de passe pouvait l'etre. */}
                        <button onClick={() => setCompteAEditer(user)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4,
                            padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${t.border}`, background: t.bgInput,
                            fontSize: 10, fontWeight: 700, color: t.textSecondary, transition: 'all 0.15s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#448696'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#448696'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = t.bgInput; e.currentTarget.style.color = t.textSecondary; e.currentTarget.style.borderColor = t.border; }}>
                          <Edit size={10} />
                          Identifiant
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className="px-6 py-4 border-t space-y-2" style={{ borderColor: t.border }}>
          <div className="grid grid-cols-2 gap-2">
            <PanelBtn icon={Edit} label="Modifier" onClick={() => { onClose(); onEdit(company); }}
              bg={t.primarySoft} border={t.primaryBorder} color={t.primaryText} hoverBg={t.primary} hoverColor="#fff" />
            <PanelBtn icon={Shield} label="Licence" onClick={() => { onClose(); onManageLicence(company); }}
              bg={t.primarySoft} border={t.primaryBorder} color={t.primaryText} hoverBg={t.primary} hoverColor="#fff" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            {/* Dupliquer : creer un client a l'image de celui-ci — son offre,
                ses extensions, son parametrage. Jamais ses donnees. */}
            <PanelBtn icon={Copy} label="Dupliquer ce client" onClick={() => { onClose(); onDupliquer(company); }}
              bg={t.primarySoft} border={t.primaryBorder} color={t.primaryText} hoverBg={t.primary} hoverColor="#fff" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {company.is_blocked ? (
              <PanelBtn icon={Unlock} label="Débloquer" onClick={() => onUnblock(company)} disabled={loading}
                bg={t.successSoft} border={t.successText + '30'} color={t.successText} hoverBg={t.success} hoverColor="#fff" />
            ) : (
              <PanelBtn icon={Lock} label="Bloquer" onClick={() => onBlock(company)} disabled={loading}
                bg={t.warningSoft} border={t.warningText + '30'} color={t.warningText} hoverBg={t.warning} hoverColor="#fff" />
            )}
            <PanelBtn icon={Trash2} label="Supprimer" onClick={() => onDelete(company)} disabled={loading}
              bg={t.dangerSoft} border={t.dangerText + '30'} color={t.dangerText} hoverBg={t.danger} hoverColor="#fff" />
          </div>
        </div>
      </div>

      {compteAEditer && (
        <IdentifiantModal
          company={company}
          user={compteAEditer}
          t={t}
          onClose={() => setCompteAEditer(null)}
          onSaved={(compte) => {
            setUsers((liste) => liste.map((u) => (u.id === compteAEditer.id ? { ...u, ...compte } : u)));
            setCompteAEditer(null);
          }} />
      )}

      {/* Modal résultat reset */}
      {resetResult && (
        <ResetResultModal result={resetResult} t={t} onClose={() => setResetResult(null)} />
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const CompanyList = ({ companies, onCompanyUpdated, onManageLicence, theme: themeProp }) => {
  const ctxTheme = useTheme();
  const t = themeProp || ctxTheme;

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [clientADupliquer, setClientADupliquer] = useState(null);
  const [editingCompany,  setEditingCompany]  = useState(null);
  const [loading,         setLoading]         = useState(null);
  const [confirmDialog,   setConfirmDialog]   = useState({ isOpen: false, type: null, company: null });
  const [toast,           setToast]           = useState({ show: false, message: '', type: '' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  const handleBlock = async (company) => {
    setLoading(company.id);
    try { await superAdminApi.companies.block(company.id); showToast(`${company.company_name} bloquée`); setSelectedCompany(null); onCompanyUpdated(); }
    catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(null); }
  };

  const handleUnblock = async (company) => {
    setLoading(company.id);
    try { await superAdminApi.companies.unblock(company.id); showToast(`${company.company_name} débloquée`); setSelectedCompany(null); onCompanyUpdated(); }
    catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(null); }
  };

  const handleDelete = async () => {
    const company = confirmDialog.company;
    setLoading(company.id);
    try { await superAdminApi.companies.delete(company.id); showToast(`${company.company_name} supprimée`); setSelectedCompany(null); onCompanyUpdated(); }
    catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(null); setConfirmDialog({ isOpen: false, type: null, company: null }); }
  };

  if (companies.length === 0) return (
    <div className="py-16 text-center">
      <div className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: t.primarySoft }}>
        <Building2 size={28} style={{ color: t.primary }} />
      </div>
      <h3 className="text-base font-bold mb-1" style={{ color: t.text }}>Aucune entreprise trouvée</h3>
      <p className="text-sm font-medium" style={{ color: t.textMuted }}>Aucune entreprise ne correspond à vos critères</p>
    </div>
  );

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `1px solid ${t.border}` }}>
              {['Entreprise', 'Statut', 'Utilisateurs', 'Créée le', 'Expiration', 'Actions'].map((h, i) => (
                <th key={h} className={`px-5 py-3 ${i === 5 ? 'text-right' : 'text-left'}`}
                  style={{ backgroundColor: t.bgTableHeader }}>
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: t.textMuted }}>{h}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => (
              <tr key={company.id} className="group transition-colors cursor-pointer"
                style={{ borderBottom: `1px solid ${t.borderLight}`, opacity: loading === company.id ? 0.4 : 1,
                  animation: 'clRowIn 0.2s ease-out both', animationDelay: `${index * 20}ms` }}
                onClick={() => setSelectedCompany(company)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.bgTableRowHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>

                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.primaryDark})` }}>
                      <Building2 size={16} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold truncate" style={{ color: t.text }}>{company.company_name}</div>
                      <div className="text-[11px] font-mono truncate" style={{ color: t.textMuted }}>{company.company_slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3"><StatusBadge company={company} t={t} /></td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Users size={14} style={{ color: t.textMuted }} />
                    <span className="text-[13px] font-bold tabular-nums" style={{ color: t.text }}>{company.user_count || 0}</span>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={13} style={{ color: t.textMuted }} />
                    <span className="text-[13px] font-medium" style={{ color: t.textSecondary }}>{formatDate(company.created_at)}</span>
                  </div>
                </td>
                <td className="px-5 py-3">
                  {company.trial_ends_at ? (
                    <div className="flex items-center gap-1.5">
                      <Clock size={13} style={{ color: t.textMuted }} />
                      <span className="text-[13px] font-medium" style={{ color: t.textSecondary }}>{formatDate(company.trial_ends_at)}</span>
                    </div>
                  ) : <span className="text-[13px]" style={{ color: t.textMuted }}>—</span>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <ActionBtn icon={Edit} label="Modifier" color={t.textMuted} hoverBg={t.primarySoft} onClick={() => setEditingCompany(company)} disabled={loading === company.id} />
                    <ActionBtn icon={Shield} label="Licence" color={t.primary} hoverBg={t.primarySoft} onClick={() => onManageLicence(company)} disabled={loading === company.id} />
                    {company.is_blocked
                      ? <ActionBtn icon={Unlock} label="Débloquer" color={t.successText} hoverBg={t.successSoft} onClick={() => handleUnblock(company)} disabled={loading === company.id} />
                      : <ActionBtn icon={Lock} label="Bloquer" color={t.warningText} hoverBg={t.warningSoft} onClick={() => setConfirmDialog({ isOpen: true, type: 'block', company })} disabled={loading === company.id} />}
                    <ActionBtn icon={Trash2} label="Supprimer" color={t.dangerText} hoverBg={t.dangerSoft} onClick={() => setConfirmDialog({ isOpen: true, type: 'delete', company })} disabled={loading === company.id} />
                  </div>
                  <div className="flex items-center justify-end group-hover:hidden">
                    <ChevronRight size={14} style={{ color: t.textMuted, opacity: 0.4 }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCompany && (
        <DetailPanel company={selectedCompany} t={t} loading={loading === selectedCompany?.id}
          onClose={() => setSelectedCompany(null)}
          onEdit={(c) => { setSelectedCompany(null); setEditingCompany(c); }}
          onManageLicence={(c) => { setSelectedCompany(null); onManageLicence(c); }}
          onBlock={(c) => setConfirmDialog({ isOpen: true, type: 'block', company: c })}
          onUnblock={handleUnblock}
          onDupliquer={(c) => setClientADupliquer(c)}
          onDelete={(c) => setConfirmDialog({ isOpen: true, type: 'delete', company: c })} />
      )}

      {clientADupliquer && (
        <DuplicationClientModal
          modele={clientADupliquer}
          onClose={() => setClientADupliquer(null)}
          onSuccess={() => { setClientADupliquer(null); onCompanyUpdated(); }} />
      )}

      {editingCompany && (
        <CompanyForm company={editingCompany} onClose={() => setEditingCompany(null)}
          onSuccess={() => { setEditingCompany(null); onCompanyUpdated(); }} />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'block'}
        onClose={() => setConfirmDialog({ isOpen: false, type: null, company: null })}
        onConfirm={() => { handleBlock(confirmDialog.company); setConfirmDialog({ isOpen: false, type: null, company: null }); }}
        title="Bloquer l'entreprise ?" message={`Voulez-vous vraiment bloquer "${confirmDialog.company?.company_name}" ?`}
        confirmText="Bloquer" cancelText="Annuler" type="warning" />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen && confirmDialog.type === 'delete'}
        onClose={() => setConfirmDialog({ isOpen: false, type: null, company: null })}
        onConfirm={handleDelete}
        title="Supprimer l'entreprise ?" message={`⚠️ ATTENTION : Supprimer "${confirmDialog.company?.company_name}" est IRRÉVERSIBLE.`}
        confirmText="Supprimer définitivement" cancelText="Annuler" type="danger" />

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}

      <style>{`
        @keyframes clRowIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </>
  );
};

export default CompanyList;